// Full-view roster table for patients_with_ehrid rows.
//
// Built with the SAME Chakra primitives, columns and styling as the Schedules
// page's data table (components/table/ResultsTable.js) so it looks identical:
// royal-blue header, centered white column labels from getColumnLabels(role),
// the same `prompts` columns, compact rows. Columns a roster patient has no
// value for (DOB, Type, Physician) render "N/A", exactly like ResultsTable
// does; the visit-scheduling columns (Scheduled Date/Time, Late By) are omitted
// entirely since a patient-level roster row has no visit. Clicking a row opens
// the patient (roster-safe — it does NOT hardcode /clinic like ResultsTable,
// which is why we can't reuse that component wholesale here).
import React, { useState } from 'react';
import { Box, Table, Thead, Tbody, Tr, Th, Td } from '@chakra-ui/react';
import PhoneStatusBadge from './sms/PhoneStatusBadge';
import MedStudentReportsModal from '../pages/medstudent/MedStudentReportsModal';
import { prompts, royal_blue } from '../components/env';
import { getColumnLabels } from '../config/roles';

// mm/dd/yyyy, mirroring ResultsTable.formatDOB — "N/A" when absent.
function formatDob(dobValue) {
  if (!dobValue) return 'N/A';
  if (String(dobValue).includes('/')) return dobValue;
  const m = String(dobValue).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!m) return dobValue;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return dobValue;
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function cellValue(row, field) {
  if (field === 'dob') return formatDob(row.dob);
  const v = row[field];
  return v === undefined || v === null || v === '' ? 'N/A' : v;
}

function hasReports(row) {
  return typeof row?.reports === 'string' && row.reports.trim().length > 0;
}

const thProps = { color: 'white', textAlign: 'center', py: 2, px: 2, whiteSpace: 'nowrap' };
const tdProps = { textAlign: 'center', py: 2, px: 2 };

export default function RosterTable({ rows = [], onPatientClick = () => {}, isLoading = false, userRole = 'doctor', homeSchema = null }) {
  const [reportsPatient, setReportsPatient] = useState(null);
  const columnLabels = getColumnLabels(userRole) || {};
  const label = (field, fallback) => columnLabels[field] || fallback;

  if (isLoading) {
    return <Box p={6} textAlign="center" color="gray.500" fontSize="sm">Loading…</Box>;
  }
  if (!rows.length) {
    return <Box p={6} textAlign="center" color="gray.500" fontSize="sm">No patients found.</Box>;
  }

  return (
    <Box className="schedules-container" width="100%" overflowX="auto" p={2}>
      <Table colorScheme="gray" size="sm">
        {/* Header — same column set & order as ResultsTable's default view:
            first 5 prompt fields, Submissions, remaining prompt fields, then
            the Scheduled/tooling columns and Reports. (The Schedules table's
            leading "Select" checkbox is a bulk-select control, not a data
            column, so it's intentionally omitted.) */}
        <Thead bg={royal_blue}>
          <Tr>
            {prompts.slice(0, 5).map(([field, rawLabel]) => (
              <Th key={field} {...thProps}>{label(field, rawLabel)}</Th>
            ))}
            <Th {...thProps}>Submissions</Th>
            {prompts.slice(5).map(([field, rawLabel]) => (
              <Th key={field} {...thProps}>{label(field, rawLabel)}</Th>
            ))}
            <Th {...thProps}>Audio Tools</Th>
            <Th {...thProps}>Orders</Th>
            <Th {...thProps}>Reports</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row) => {
            const showReports = hasReports(row);
            const naCell = (key) => <Td key={key} {...tdProps} color="gray.400">N/A</Td>;
            return (
              <Tr
                key={row.id}
                onClick={() => onPatientClick(row)}
                cursor="pointer"
                _hover={{ bg: 'blue.50' }}
                sx={{ bg: 'white', borderBottom: '1px solid', borderColor: 'gray.100' }}
              >
                {prompts.slice(0, 5).map(([field]) => (
                  <Td key={field} {...tdProps} fontWeight={field === prompts[0][0] ? 'medium' : 'normal'}>
                    {cellValue(row, field)}
                    {(field === 'tel_num' || field === 'home_phone') && <PhoneStatusBadge phone={row[field]} compact ml={1} />}
                    {/* Foreign-provider badge — only the linked-schema rows a
                        cross-schema roster fan-out pulled in, not the
                        viewer's own (home) schema. Lives on the name cell. */}
                    {field === prompts[0][0] && row._schema && row._schema !== homeSchema && (
                      // Inline styles (not Tailwind): this badge sits inside a
                      // Chakra <Td>, where Tailwind utilities can be swallowed by
                      // Chakra's reset/specificity. Matches LeejoeSchedulesView's
                      // indigo chip (indigo-100 bg / indigo-700 text) exactly.
                      <span style={{ marginLeft: 8, display: 'inline-block', fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: '4px', backgroundColor: '#e0e7ff', color: '#4338ca', verticalAlign: 'middle' }}>{row._schema}</span>
                    )}
                  </Td>
                ))}
                {naCell('submissions')}
                {prompts.slice(5).map(([field]) => (
                  <Td key={field} {...tdProps}>
                    {cellValue(row, field)}
                    {(field === 'tel_num' || field === 'home_phone') && <PhoneStatusBadge phone={row[field]} compact ml={1} />}
                  </Td>
                ))}
                {['audio-tools', 'orders'].map(naCell)}
                <Td {...tdProps} onClick={(e) => e.stopPropagation()}>
                  {showReports ? (
                    <button
                      type="button"
                      onClick={() => setReportsPatient(row)}
                      className="inline-flex items-center rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold px-2.5 py-1 shadow-sm transition-colors"
                    >
                      Reports
                    </button>
                  ) : (
                    <Box as="span" color="gray.300">N/A</Box>
                  )}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      <MedStudentReportsModal
        isOpen={!!reportsPatient}
        onClose={() => setReportsPatient(null)}
        patient={reportsPatient}
      />
    </Box>
  );
}
